import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

const Navbar = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const isAdmin = location.pathname.startsWith('/admin');

  const handleLogout = () => {
    logout();
    navigate('/');
  };


  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setMobileMenuOpen(false); // Close mobile menu when clicking a nav item
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    } else {
      scrollToSection(sectionId);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    // Prevent body scroll when mobile menu is open
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    // Clear active section if not on home page
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    // Update active section based on hash
    if (location.hash) {
      const sectionId = location.hash.substring(1);
      setActiveSection(sectionId);
      setTimeout(() => scrollToSection(sectionId), 100);
    }

    // Handle scroll to update active section (only on home page)
    const handleScroll = () => {
      if (location.pathname !== '/') return;
      
      const sections = ['event-details', 'sponsors', 'registrants'];
      const scrollPosition = window.scrollY + 150;

      // Check if we're above the first section (in hero area)
      const firstSection = document.getElementById(sections[0]);
      if (firstSection && scrollPosition < firstSection.offsetTop - 200) {
        setActiveSection('');
        return;
      }

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  return (
    <>
      <nav className="navbar">
        <div className="navbar-top">
          <div className="navbar-top-container">
          <Link to="/" className="navbar-brand">
            <div className="navbar-icon">
              <img src="/toke-nav-logo.png" alt="Logo" className="navbar-logo-img" />
            </div>
            <span className="navbar-title">Toke For Stroke Open</span>
          </Link>
            <div className="navbar-actions">
              {isAuthenticated && (
                <button onClick={handleLogout} className="navbar-signin">
                  SIGN OUT
                </button>
              )}
              <button 
                className="navbar-hamburger" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6 L21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 12 L21 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M3 18 L21 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        {!isAdmin && (
          <div className="navbar-bottom">
            <div className="navbar-bottom-container">
              <a 
                href="#event-details"
                onClick={(e) => handleNavClick(e, 'event-details')}
                className={`navbar-nav-link ${activeSection === 'event-details' ? 'active' : ''}`}
              >
                EVENT DETAILS
              </a>
              <Link 
                to="https://teetimetony.com/products/toke-for-stroke-1-player?variant=51510043246874"
                className={`navbar-nav-link ${location.pathname === '/register' ? 'active' : ''}`}
              >
                REGISTER
              </Link>
              <a 
                href="#sponsors"
                onClick={(e) => handleNavClick(e, 'sponsors')}
                className={`navbar-nav-link ${activeSection === 'sponsors' ? 'active' : ''}`}
              >
                SPONSORS
              </a>
              <a 
                href="#registrants"
                onClick={(e) => handleNavClick(e, 'registrants')}
                className={`navbar-nav-link ${activeSection === 'registrants' ? 'active' : ''}`}
              >
                REGISTRANTS
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Side Navigation */}
      <div 
        className={`sidenav-overlay ${mobileMenuOpen ? 'open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div className={`sidenav ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidenav-header">
          <div className="sidenav-icon-wrapper">
            <img src="/toke-nav-logo.png" alt="Logo" className="sidenav-logo-img" />
          </div>
          <div className="sidenav-title-group">
            <div className="sidenav-title-line">TOKE FOR</div>
            <div className="sidenav-title-line">STROKE</div>
            <div className="sidenav-title-line">OPEN</div>
          </div>
        </div>
        <nav className="sidenav-menu">
          <a 
            href="#event-details"
            onClick={(e) => handleNavClick(e, 'event-details')}
            className={`sidenav-item ${activeSection === 'event-details' ? 'active' : ''}`}
          >
            Event Details
          </a>
          <Link 
            to="https://teetimetony.com/products/toke-for-stroke-1-player?variant=51510043246874"
            className={`sidenav-item ${location.pathname === '/register' ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Register
          </Link> 
          <a 
            href="#sponsors"
            onClick={(e) => handleNavClick(e, 'sponsors')}
            className={`sidenav-item ${activeSection === 'sponsors' ? 'active' : ''}`}
          >
            Sponsors
          </a>
          <a 
            href="#registrants"
            onClick={(e) => handleNavClick(e, 'registrants')}
            className={`sidenav-item ${activeSection === 'registrants' ? 'active' : ''}`}
          >
            Registrants
          </a>
        </nav>
        <div className="sidenav-footer">
          <div className="sidenav-footer-icon">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <path d="M10 8.5 L10 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M7 17 L13 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;

